//////////////////////////////////////////////////////////////////////////////
// GLG RealTimeChart example demonstrates how to use a custom chart widget 
// containing a toolbar allowing to scroll and zoom the chart, as well
// as change the time interval on the X axis for viewing the chart data. 
//
// DemoDataFeed.js supplies simulated data for animation. An application 
// should provide a custom implementation of LiveDataFeed.js to supply 
// real-time application data to the chart. Refer to README.txt for more
// information.
//
// Real-time data for the chart are queried based on tags assigned to the
// plots in a chart. Tags are added to the ValueEntryPoint of each plot 
// in the drawing at design time, and a valid TagSource should be assigned 
// for each tag. Tag sources represent data source variables used to query 
// data for the plots and are used by the DataFeed object to query 
// real-time data for for the chart.
//////////////////////////////////////////////////////////////////////////////

/* eslint eqeqeq: 0, no-unused-vars: 0 */
/* eslint @typescript-eslint/no-unused-vars: 0 */

import { GLG, IsUndefined, GetCurrTime,
         AppAlert, AppError, AppLog } from './Utils.js'
import { DemoDataFeed } from './DemoDataFeed.js'
import { LiveDataFeed } from './LiveDataFeed.js'
import { PlotDataPoint, PlotInfo } from './PlotData.js'

// Enable general debugging/diagnostics information.
const DEBUG = false;

/* Debugging: set the variable to true to throw an exception on a GLG error
   instead of just displaying an error message on the console.
*/
const DEBUG_GLG_ERRORS = false;

/* stripchart.g displays a horizontal stripchart.
   Change to stripchart_vertical.g to display a vertical stripchart.
*/
const DRAWING_NAME = "stripchart.g"

/* Is set to true, simulated demo data will be used for animation.
   Set to false to enable live application data generated by GetAppData().
*/
const RANDOM_DATA = true;

// Index of the initial chart span to display, used by SetChartSpan().
const INIT_SPAN = 0;    /* 0 => ONE_MINUTE */

/* If set to true, the application supplies time stamp for each data 
   sample explicitly in DataFeed.GetPlotPoint(). Otherwise, time stamp 
   is automatically generated by the chart using current time.
*/
const SUPPLY_TIME_STAMP = false;

// Time span constants.
const ONE_MINUTE = 60;
const ONE_HOUR   = 3600;
const ONE_DAY    = 3600 * 24;

/* Constants for scrolling the chart to the beginning or the end of 
   the time range.
*/
const DONT_CHANGE  = 0;
const MOST_RECENT  = 1;  /* Make the most recent data visible. */
const LEAST_RECENT = 2;  /* Make the least recent data visible.*/

//////////////////////////////////////////////////////////////////////////////
// Creates an instance of the real time chart.
// Parameters:
//   glg_div_name  - name of parent div the drawing will be displayed in,
//                   will be passed by the caller.
//   is_mobile     - true if deployed on mobile devices.
//   is_standalone - true if deployed in html, false if deployed in react or
//                   angular.
//   glg_path      - path to the directory where GLG drawings are located.
//   title         - an optional title string
//////////////////////////////////////////////////////////////////////////////
export function GlgRealTimeChart( glg_div_name, glg_path,
                                  is_standalone, is_mobile, title )
{
   Debug( "New script for " + glg_div_name + ": "  +
          Object.getPrototypeOf( this ).constructor.name +
          " glg_path: " + glg_path );
   
   if( DEBUG_GLG_ERRORS )   
     GLG.ThrowExceptionOnError( true, true, true );
   else
     GLG.ThrowExceptionOnError( false, false, false );

   this.GLG_div_name = glg_div_name;
   this.IsMobile = is_mobile;
   this.Standalone = is_standalone;    
   
   // Use path to the drawings directory is supplied.
   this.GlgPath = glg_path;
     
   // Use title if supplied. 
   this.Title = title;

   this.DrawingName = DRAWING_NAME;

   // DataFeed object used for animation.
   this.DataFeed = null;  

   // Top level viewport of the loaded drawing (GlgObject).
   this.MainViewport = null;

   // Viewport containing the Chart oject.
   this.ChartVP = null;          /* GlgObject */

   // Chart object inside ChartVP.
   this.Chart = null;

   /* An array of PlotInfo objects. Stores information about each plot in a
      chart.
   */
   this.PlotArray = null;        /* PlotInfo[] */

   // Number of plots.
   this.NumPlots = 0;            /* int */

   // Number of Y axes.
   this.NumYAxes = 0;            /* int */

   // Arrays storing low and high ranges for all Y axes.
   this.Low = null;             /* double[] */
   this.High = null;            /* double[] */

   // Toolbar object inside the chart drawing.
   this.Toolbar = null;         /* GlgObject */

   /* Contains a list of tag sources defined in the drawing, which are used by 
      the DataFeed object to query real-time data for the chart plots.
      The array is populated in CreateDataTagList(), and each element has
      a property 'tag_source'. 
   */
   this.DataTagList = null;   /* [] */

   // Update interval in msec.
   this.UpdateInterval = 100;  
    
   /* Prefill time interval, specifies amount of data to prefill in the 
      real time chart. 
   */
   this.PrefillSpan = ONE_HOUR * 8;

   /* Set index of the currently displayed time span to the inital span index.
      Used by SetChartSpan().
   */
   this.SpanIndex = INIT_SPAN; 

   this.TimeSpan = 0; // Time axis span in seconds, set by SetChartSpan().
   
   /* If set to true, the chart's data buffer is prefilled with historical data
      on start-up.
   */ 
   this.PrefillData = true;

   // Containes the number of already prefilled plots. 
   this.NumPrefilledPlots = 0;
   
   // int: Current auto-scroll state: enabled (1) or disabled (0).
   this.AutoScroll = 1;

   // int: Stored AutoScroll state to be restored if ZoomTo is aborted.
   this.StoredScrollState = null;

   /* Coefficient for canvas resolution. It will be adjusted in 
      SetCanvasResolution() for mobile devices with HiDPI displays as well as 
      on browser zoom.
   */
   this.CoordScale = 1;
}

////////////////////////////////////////////////////////////////////////////// 
// Script entry point: Starts the chart by loading its drawing.
////////////////////////////////////////////////////////////////////////////// 
GlgRealTimeChart.prototype.Start = function()
{
   Debug( "Starting: " + this.GLG_div_name );

   this.Active = true;
   
   // Set initial size of the drawing.
   this.SetDrawingSize( false );

   /* Load misc. assets such as GLG scrollbars. When assets are loaded, 
      LoadDrawing callback is invoked that loads a GLG drawing defined by
      DrawingName variable.
   */
   this.LoadAssets( ()=>this.LoadDrawing(), null );
   
   // Add DataFeed object used to animate the drawing with real-time data.
   this.AddDataFeed();
}

////////////////////////////////////////////////////////////////////////////// 
// Script entry point: Performs cleanup.
////////////////////////////////////////////////////////////////////////////// 
GlgRealTimeChart.prototype.Cleanup = function()
{
   Debug( "Cleanup for: " + this.GLG_div_name );

   this.Active = false;    // Ignore any pending updates and callbacks.

   if( this.MainViewport != null )
     this.MainViewport.ResetHierarchy();  // Undisplay GLG drawing.

   if( this.DataFeed )
     this.DataFeed.Cleanup();              // Cleanup DataFeed.

   if( this.ResizeListener )
     window.removeEventListener( "resize", this.ResizeListener );
}

////////////////////////////////////////////////////////////////////////////// 
// Load a GLG drawing from a file.
////////////////////////////////////////////////////////////////////////////// 
GlgRealTimeChart.prototype.LoadDrawing = function()
{
   Debug( "LoadDrawing for: " + this.GLG_div_name );
   
   if( !this.Active )
     return;

   /* Load a drawing from the specified drawing file. 
      The LoadCB callback will be invoked when the drawing has been loaded.

      Using "bind( this )" as a shorter way to provide "this" compared with 
      using lambda: with bind, we do not need to specify parameter list that
      we would need to provide for lambda.
   */
   GLG.LoadWidgetFromURL( this.GetFullName( this.DrawingName ), null,
                          this.LoadCB.bind( this ), /*user data*/ null,
                          /*abort test function*/ ()=>!this.Active );
}

////////////////////////////////////////////////////////////////////////////// 
GlgRealTimeChart.prototype.GetFullName = function( drawing_name )
{
   if( this.GlgPath == null )
     return drawing_name;

   return this.GlgPath + "/" + drawing_name;
}
   
//////////////////////////////////////////////////////////////////////////////
GlgRealTimeChart.prototype.LoadCB =
  function( /*GlgObject*/ drawing, /*Object*/ user_data, /*String*/ path )
{
   Debug( "LoadCB for: " + this.GLG_div_name );

   if( !this.Active )
     return;

   if( drawing == null )
   {
      AppAlert( "Can't load drawing, check console message for details." );
      return;
   }
   
   if( !document.getElementById( this.GLG_div_name ) )
   {
      Debug( "Can't find " + this.GLG_div_name +
             " div: it may have been removed from the document." );
      return;
   }
    
   // Disable spinning loader.   
   RemoveElement( this.GLG_div_name, "loader_container" );
    
   // Define the element in the HTML page to display the drawing.
   drawing.SetParentElement( this.GLG_div_name );
    
   // Disable viewport border to use the border of the glg_area.
   if( this.Standalone )
     drawing.SetDResource( "LineWidth", 0 );
    
   this.StartGlgChart( drawing );
}

//////////////////////////////////////////////////////////////////////////////
GlgRealTimeChart.prototype.StartGlgChart = function( /*GlgObject*/ drawing )
{
   this.MainViewport = drawing;

   // Initialization before hierarchy setup.
   this.InitBeforeH();

   // Setup object hierarchy of the drawing.
   this.MainViewport.SetupHierarchy();

   // Initialization after hierarchy setup.
   this.InitAfterH();

   /* Start dynamic updates. If prefilling, it will be started after prefilling
      data has finished.
   */
   if( !this.PrefillData )
     this.GetData();

   // Display the drawing in a web page.
   this.MainViewport.Update();
}

//////////////////////////////////////////////////////////////////////////////
// Initialization before hierarchy setup.
//////////////////////////////////////////////////////////////////////////////
GlgRealTimeChart.prototype.InitBeforeH = function()
{
   // Add Input and Trace callbacks.
   this.MainViewport.AddListener( GLG.GlgCallbackType.INPUT_CB,
                                  this.InputCallback.bind( this ) );
   this.MainViewport.AddListener( GLG.GlgCallbackType.TRACE_CB,
                                  this.TraceCallback.bind( this ) );
    
   // Retrieve object ID of the Toolbar viewport.
   this.Toolbar = this.MainViewport.GetResourceObject( "Toolbar" );
   if( this.Toolbar == null )
     AppAlert( "Toolbar object not found. " );

   // Retrieve ChartViewport object.
   this.ChartVP = this.MainViewport.GetResourceObject( "ChartViewport" );
   if( this.ChartVP == null )
   {
      AppAlert( "ChartViewport not found. " );
      return;
   }

   if( this.Title )
     // Set title is supplied.
     this.ChartVP.SetSResource( "Title/String", this.Title );
   
   // Retrieve the Chart object.
   this.Chart = this.ChartVP.GetResourceObject( "Chart" );
   if( this.Chart == null )
   {
      AppAlert( "Chart object not found. " );
      return;
   }

   // Retrieve the number of plots defined in the drawing.
   this.NumPlots = Math.trunc( this.Chart.GetDResource( "NumPlots" ) );
    
   // Retrieve the number of Y axes defined in the drawing.
   this.NumYAxes = Math.trunc( this.Chart.GetDResource( "NumYAxes" ) );
    
   // Enable AutoScroll, both for the toggle button and the chart.
   this.ChangeAutoScroll( 1 );

   /* Set Chart Zoom mode. It was set and saved with the drawing, 
      but do it again programmatically just in case.
   */
   this.ChartVP.SetZoomMode( null, this.Chart, null,
                             GLG.GlgZoomMode.CHART_ZOOM_MODE );
    
   /* Uncomment the line below to override XAxis label TimeFormat 
      defined in the drawing. "%T" displays time without a date.
   */
   //this.Chart.SetSResource( "XAxis/TimeFormat", "%T" );
}

//////////////////////////////////////////////////////////////////////////////
// Initialization after hierarchy setup.
//////////////////////////////////////////////////////////////////////////////
GlgRealTimeChart.prototype.InitAfterH = function()
{
   let plot, value_ep, time_ep, valid_ep; /* GlgObject */
   let tag_source;                        /* String */

   // Retrieve the a group of plots from the chart.
   let plots = this.Chart.GetResourceObject( "Plots" );  /* GlgObject  */

   // Allocate PlotArray and store information for each plot. 
   this.PlotArray = [];

   /* Populate PlotArray with PlotInfo objects, storing information
      for each plot.
   */
   for( let i=0; i<this.NumPlots; ++i )
   {
      plot = plots.GetElement( i ); 
      value_ep = plot.GetResourceObject( "ValueEntryPoint" );
      time_ep = plot.GetResourceObject( "TimeEntryPoint" );
      valid_ep = plot.GetResourceObject( "ValidEntryPoint" );

      if( value_ep.HasResourceObject( "TagObject" ) )
        tag_source = value_ep.GetSResource( "TagSource" );
      else
        tag_source = null;

      // Skip plots with invalid TagSource.        
      if( IsUndefined( tag_source ) )
      {
         AppAlert( "Invalid TagSource for Plot# " + i );
         continue;
      }

      // Add new plot record to PlotArray.
      this.PlotArray.push( new PlotInfo( plot, value_ep, time_ep, valid_ep,
                                         tag_source ) ); 
   }
   
   // Store valid tag sources in DataTagList array.
   this.CreateDataTagList();

   /* Store initial range for each Y axis to restore on zoom reset. 
      Assumes that plots are linked with the corresponding axes in the 
      drawing.
   */         
   this.Low = new Array( this.NumYAxes );    /* double[] */
   this.High = new Array( this.NumYAxes );   /* double[] */
    
   let axis_array =
     this.Chart.GetResourceObject( "YAxisGroup" ); /* GlgObject */
   for( let i=0; i<this.NumYAxes; ++i )
   {
      let axis = axis_array.GetElement( i );   /* GlgObject */
      this.Low[i] = axis.GetDResource( "Low" );
      this.High[i] = axis.GetDResource( "High" );
   }
    
   // Set chart's time span on the X axis.
   this.SetChartSpan( this.SpanIndex );
    
   if( this.PrefillData )
   {
      /* Prefill chart's history buffer with data for a specified number 
         of seconds. Invoke it on a timeout to let the browser to show
         the chart with the "Prefilling data..." message first, then
         prefill the data and erase the message.
      */
      setTimeout( ()=>this.FillChartHistory( this.PrefillSpan ), 10 );
      this.ShowPrefillMessage( true );
   }
   else
     this.ShowPrefillMessage( false );
}

//////////////////////////////////////////////////////////////////////////////
GlgRealTimeChart.prototype.ShowPrefillMessage = function( state )
{
   if( this.MainViewport.HasResourceObject( "ChartViewport/PreFillMessage" ) )
     this.MainViewport.SetDResource( "ChartViewport/PreFillMessage/Visibility",
                                     state ? 1. : 0 );
}
   
//////////////////////////////////////////////////////////////////////////////
// Create DataFeed object used to animate the chart with data.
// Simulated data are provided by DemoDataFeed. The application should 
// provide a custom implementation of LiveDataFeed.js to supply real-time
// application data for the chart.
//////////////////////////////////////////////////////////////////////////////
GlgRealTimeChart.prototype.AddDataFeed = function()
{
   if( RANDOM_DATA )
   {
      this.DataFeed = new DemoDataFeed();
      AppLog( "Using random DemoDataFeed." );
   }
   else
   {
      this.DataFeed = new LiveDataFeed();
      AppLog( "Using LiveDataFeed." );
   }
}

//////////////////////////////////////////////////////////////////////////////
GlgRealTimeChart.prototype.CreateDataTagList = function()
{
   if( this.PlotArray == null || this.PlotArray.length == 0 )
   {
      this.DataTagList = null;
      return;
   }

   this.DataTagList = [];   /* String[] */
   for( let i=0; i<this.PlotArray.length; ++i )
   {
      // Store only valid tag sources, skipping unassigned tags.
      if( !IsUndefined( this.PlotArray[i].tag_source ) )
        this.DataTagList.push( { tag_source: this.PlotArray[i].tag_source } );
   }
}

////////////////////////////////////////////////////////////////////////////// 
// Pre-fill the graph's history buffer with data. 
////////////////////////////////////////////////////////////////////////////// 
GlgRealTimeChart.prototype.FillChartHistory = function( /*int*/ prefill_span )
{
   if( !this.Active )
     return;

   this.ShowPrefillMessage( false );

   if( this.PlotArray == null || this.PlotArray.length == 0 )
     return;

   let current_time = GetCurrTime();   /* double */
    
   /* Fill the amount of data requested by the PrefillSpan, up to the 
      available chart's buffer size defined in the drawing.
      Add an extra second to avoid rounding errors.
   */
   let num_seconds = prefill_span + 1;  /* int */
    
   let buffer_size =     /* int */
     Math.trunc( this.Chart.GetDResource( "BufferSize" ) );
   if( buffer_size < 1 )
     buffer_size = 1;
    
   let max_num_samples;   /* int */
   if( RANDOM_DATA )
   {
      // In random demo data mode, simulate data stored once per second.
      let samples_per_second = 1.0;   /* double */
      max_num_samples = Math.trunc( num_seconds * samples_per_second );
        
      if( max_num_samples > buffer_size )
        max_num_samples = buffer_size;
   }
   else
     max_num_samples = buffer_size;
    
   // Start and end time for the data query.
   let start_time = current_time - num_seconds;   /* double */
   let end_time = current_time;     /* double : Stop at the current time. */

   /* Obtain historical data for the plot based on the tag assigned
      to the plot's ValueEntryPoint.
   */
   for( let i=0; i<this.PlotArray.length; ++i )
     this.DataFeed.GetHistPlotData( this.PlotArray[i].tag_source, 
                                    start_time, end_time, max_num_samples,
                                    /*callback*/ PlotHistDataCB.bind( this ), 
                                    /*user data*/ this.PlotArray[i].plot );
}

////////////////////////////////////////////////////////////////////////////// 
// Fills a plot with data from the provided data array.
// For increased performance of prefilling a chart with large quantities of
// data, the data are pushed into the plot using a low level API method
// AddPlotDataSample.
////////////////////////////////////////////////////////////////////////////// 
function PlotHistDataCB( /*PlotDataPoint[]*/ data_array, /*GlgObject*/ plot )
{
   if( data_array == null )
     return;

   if( plot.GetDResource( "HISetup" ) == 0 )
   {
      Debug( "The chart plot is unset: the chart may have been removed "
             + "from the document." );
      return;
   }
   
   let size = data_array.length;
   for( let i=0; i<size; ++i )
   {
      let data_point = data_array[i];   /* PlotDataPoint */
       
      /* Using quick mode to speed up prefilling of the chart, break out if
         an error is detected.
      */
      if( !plot.AddPlotDataSample( data_point.value, data_point.time_stamp,
                                   data_point.value_valid, /*no marker*/ 0,
                                   /*quick mode*/ true ) )
          break;
   }

   // Check if all plots have been prefilled.
   ++this.NumPrefilledPlots;
   if( this.NumPrefilledPlots == this.PlotArray.length )
   {
      this.ScrollToDataEnd( MOST_RECENT, true );

      // Handle chart autoscale if any.
      this.Chart.UpdateChartState( null,
                                   GLG.GlgChartState.CHART_AUTOSCALE_STATE );
      this.MainViewport.Update();
      
      // Start data updates after prefilling all plots with data.
      this.GetData();
   }
}

//////////////////////////////////////////////////////////////////////////////
GlgRealTimeChart.prototype.GetData = function()
{
   if( !this.Active || this.DataFeed == null )
     return;

   /* Obtain new real-time data values for all plots in the chart and invoke
      GetDataCB callback when done. Tags assigned to the ValueEntryPoint of 
      each plot represent data source variables to query real-time values for.
   */
   this.DataFeed.ReadData( this.DataTagList,
                           this.GetDataCB.bind( this ) /*callback*/,
                           /*user data*/ null );
}

//////////////////////////////////////////////////////////////////////////
// Data query callback. It is invoked by the DataFeed after the new data 
// are received from the server.
//////////////////////////////////////////////////////////////////////////
GlgRealTimeChart.prototype.GetDataCB = function( new_data )
{
   if( !this.Active )
     return;   // Chart was deleted: ignore received data.
   
   // Push new data to the chart.
   this.UpdateChart( new_data );
    
   // Send new data query request.
   setTimeout( this.GetData.bind( this ), this.UpdateInterval );
}

//////////////////////////////////////////////////////////////////////////////
// Push new data into graphics. For each tag in new_data array, find a
// a record in PlotArray with a matching tag source, store the new value, 
// and push new value into graphics.  
//////////////////////////////////////////////////////////////////////////////
GlgRealTimeChart.prototype.UpdateChart = function( new_data )
{
   if( new_data == null || new_data.length == 0 )
   {
      AppLog( "No new data received." );
      return;
   }

   for( let i=0; i<new_data.length; ++i )
   {
      /* Find a plot record in PlotArray that has a tag_source matching 
         new_data[i].tag_source.
      */
      let plot_record = this.LookupPlotArray( new_data[i].tag_source );

      // Plot record not found, don't use this data point.
      if( plot_record == null )
      {
         AppError( "Discarding data point, plot not found for TagSource = " 
                   + new_data[i].tag_source );
         continue;
      }

      let value = new_data[i].value;
      let time_stamp = new_data[i].time_stamp;
      let value_valid = this.DataFeed.IsValid( new_data[i].tag_source, value );

      // Push new data value into the chart plot using plot's entry points.
      plot_record.value_ep.SetDResource( null, value );
      plot_record.valid_ep.SetDResource( null, value_valid ? 1. : 0. );

      /* Push time stamp if provided. Otherwise, the chart will
         automatically use current time as a time stamp for the X axis.
      */
      if( time_stamp != null && SUPPLY_TIME_STAMP )
        plot_record.time_ep.SetDResource( null, time_stamp );
   }

   // Refresh display.
   this.MainViewport.Update();
}

//////////////////////////////////////////////////////////////////////////////
// Find a plot record in PlotArray which has a tag source matching
// the specified tag_source.
//////////////////////////////////////////////////////////////////////////////
GlgRealTimeChart.prototype.LookupPlotArray =
  function( /*String*/ tag_source )  /* PlotInfo */
{
   if( this.PlotArray == null )
     return null;

   for( let i=0; i<this.PlotArray.length; ++i )
   {
      if( this.PlotArray[i].tag_source == tag_source )
        return this.PlotArray[i];
   }

   return null; // not found.
}

//////////////////////////////////////////////////////////////////////////////
// Handle user interaction as needed.
//////////////////////////////////////////////////////////////////////////////
GlgRealTimeChart.prototype.InputCallback = function( viewport, message_obj )
{
   if( !this.Active )
     return;
    
   let origin = message_obj.GetSResource( "Origin" );
   let format = message_obj.GetSResource( "Format" );
   let action = message_obj.GetSResource( "Action" );
   let subaction = message_obj.GetSResource( "SubAction" );
    
   if( format == "Button" )
   {	 
      if( action !="Activate" &&         /* Not a push button */
          action != "ValueChanged" )     /* Not a toggle button */
        return;
        
      // Abort ZoomTo mode, if any.
      this.AbortZoomTo();
        
      if( origin == "ToggleAutoScroll" )
      {         
         /* Set Chart AutoScroll based on the ToggleAutoScroll toggle button 
            setting.
         */
         this.ChangeAutoScroll( -1 ); 
      }
      else if( origin == "ZoomTo" )
      {
         // Start ZoomTo operation.
         this.ChartVP.SetZoom( null, 't', 0.0 );  
      }
      else if( origin == "ZoomReset" )
      {         
         // Set initial time span and reset initial Y ranges.
         this.SetChartSpan( this.SpanIndex );  
         this.RestoreInitialYRanges();   
      }
      else if( origin == "ScrollBack" )
      {
         this.ChangeAutoScroll( 0 );
            
         // Scroll left by 1/3 of the span.
         this.ChartVP.SetZoom( null, 'l', 0.33 );
      }
      else if( origin == "ScrollForward" )
      {
         this.ChangeAutoScroll( 0 );
            
         // Scroll right by 1/3 of the span.
         this.ChartVP.SetZoom( null, 'r', 0.33 );
      }
      else if( origin == "ScrollBack2" )
      {
         this.ChangeAutoScroll( 0 );
            
         // Scroll left by a full span.
         this.ChartVP.SetZoom( null, 'l', 1.0 );
      }
      else if( origin == "ScrollForward2" )
      {
         this.ChangeAutoScroll( 0 );
            
         // Scroll right by a full span.
         this.ChartVP.SetZoom( null, 'r', 1.0 );
      }
      else if( origin == "ZoomIn" )
      {
         // Zoom in in Y direction.
         this.ChartVP.SetZoom( null, 'I', 1.5 );
      }
      else if( origin == "ZoomOut" )
      {
         // Zoom out in Y direction.
         this.ChartVP.SetZoom( null, 'O', 1.5 );
      }
      else if( origin == "ScrollToRecent" )
      {
         // Scroll to show most recent data.
         this.ScrollToDataEnd( MOST_RECENT, true );
      }
        
      viewport.Update();  //format = "Button"
   }

   else if( format == "Option" )
   {
      if( action != "Select" )
        return;

      // Abort ZoomTo mode, if any.
      this.AbortZoomTo();

      /* Handle events from the SpanSelector menu allowing to select time 
         interval for the X axis.
      */
      if( origin == "SpanSelector" )    /* Span change */
      { 
         this.SpanIndex =
           Math.trunc( message_obj.GetDResource( "SelectedIndex" ) );
            
         this.SetChartSpan( this.SpanIndex );
         this.RestoreInitialYRanges(); /* Restore if the chart was zoomed.*/
            
         /* Scroll to show the recent data to avoid showing an empty chart
            if user scrolls too much into the future or into the past.
            Invoke ScrollToDataEnd() even if AutoScroll is True to scroll ahead
            by a few extra seconds to show a few next updates without scrolling
            the chart.
         */
         let min_max =    /* GlgMinMax */
           this.Chart.GetChartDataExtent( null, /*x extent*/ true,
                                          /*query all samples*/ false );  
            
         if( min_max != null )
         {
            let first_time_stamp = min_max.min;   /* double */
            let last_time_stamp = min_max.max;    /* double */
            let displayed_time_end =    /* double */
              this.Chart.GetDResource( "XAxis/EndValue" );
                
            if( this.AutoScroll != 0 )
              this.ScrollToDataEnd( MOST_RECENT, true );
                
            else if( displayed_time_end >
                     last_time_stamp + this.GetExtraSeconds() )
              this.ScrollToDataEnd( MOST_RECENT, true );
                
            else if( displayed_time_end - this.TimeSpan <= first_time_stamp )
              this.ScrollToDataEnd( LEAST_RECENT, true );
                
            viewport.Update();
         }
      }
   }

   else if( format == "Chart" && action == "CrossHairUpdate" )
   {
      /* To avoid slowing down real-time chart updates, invoke Update() 
         to redraw cross-hair only if the chart is not updated fast enough 
         by the timer.
      */
      if( this.UpdateInterval > 100 )
        viewport.Update();         
   }          
  
   else if( action == "Zoom" )    // Zoom events
   {
      if( subaction == "Start" )
      {
         // Store current AutoScroll state to restore it if ZoomTo is aborted.
         this.StoredScrollState = this.AutoScroll;            
      }
      else if( subaction == "ZoomRectangle" )
      {
         // Stop scrolling when ZoomTo action is started.
         this.ChangeAutoScroll( 0 );
      }
      else if( subaction == "End" )
      {
         /* No additional actions on finishing ZoomTo. The Y scrollbar appears 
            automatically if needed: it is set to GLG_PAN_Y_AUTO. 
            Don't resume scrolling: it'll scroll too fast since we zoomed 
            in. Keep it still to allow inspecting zoomed data.
         */
      }
      else if( subaction == "Abort" )
      {
         // Resume scrolling if it was on.
         this.ChangeAutoScroll( this.StoredScrollState ); 
      }
        
      viewport.Update();
   }
   else if( action == "Pan" )    // Pan events
   {
      // Place custom code to handle pan or drag events.
   }
}

//////////////////////////////////////////////////////////////////////////////
// A custom trace callback for the page; is used to obtain coordinates 
// of the mouse click.
//////////////////////////////////////////////////////////////////////////////
GlgRealTimeChart.prototype.TraceCallback =
  function( /*GlgObject*/ viewport, /*GlgTraceData*/ trace_info )
{
   if( !this.Active )
     return;

   // Process only events that occur in ChartViewport.
   if( !trace_info.viewport.Equals( this.ChartVP ) )
     return false;

   let x, y;        /* double */
    
   let event_type = trace_info.event_type;
   switch( event_type )
   {
    case GLG.GlgEventType.TOUCH_START:
      GLG.SetTouchMode();      /* Start dragging the chart via touch events. */
      /* Fall through */
        
    case GLG.GlgEventType.TOUCH_MOVED:
      if( !GLG.GetTouchMode() )
        return;
      /* Fall through */
    case GLG.GlgEventType.MOUSE_PRESSED:
    case GLG.GlgEventType.MOUSE_MOVED:
      x = trace_info.mouse_x * this.CoordScale;
      y = trace_info.mouse_y * this.CoordScale;
         
      /* COORD_MAPPING_ADJ is added to the cursor coordinates for precise
         pixel mapping.
      */
      x += GLG.COORD_MAPPING_ADJ;
      y += GLG.COORD_MAPPING_ADJ;
      break;

    default: return;
   }
   
   switch( event_type )
   {
    case GLG.GlgEventType.TOUCH_START:
    case GLG.GlgEventType.MOUSE_PRESSED:
      if( this.ZoomToMode() )
        return;  // ZoomTo or dragging mode in progress.
        
      /* Start dragging the chart with the mouse on a mouse click. 
         If user clicked on an axis, the dragging will be activated in the
         direction of that axis. If the user clicked in the chart area,
         dragging in both the time and the Y direction will be activated. 
      */
      this.ChartVP.SetZoom( null, 's', 0.0 );
        
      // Disable AutoScroll not to interfere with dragging.
      this.ChangeAutoScroll( 0 ); 
      break;
        
    default: return;
   }
}

//////////////////////////////////////////////////////////////////////////////
// Change chart's AutoScroll mode.
//////////////////////////////////////////////////////////////////////////////
GlgRealTimeChart.prototype.ChangeAutoScroll = function( /*int*/ new_value )
{
   if( new_value == -1 )  // Use the state of the ToggleAutoScroll button.
   {
      this.AutoScroll = 
        Math.trunc( this.Toolbar.GetDResource( "ToggleAutoScroll/OnState" ) );
   }
   else    // Set to the supplied value. 
   {
      this.AutoScroll = new_value;
      this.Toolbar.SetDResource( "ToggleAutoScroll/OnState", this.AutoScroll );
   }
    
   // Set chart's auto-scroll.
   this.Chart.SetDResource( "AutoScroll", this.AutoScroll );
    
   /* Activate time scrollbar if AutoScroll is Off. The Y value scrollbar 
      uses GLG_PAN_Y_AUTO and appears automatically as needed.
   */
   let pan_x =    /* int */
     ( this.AutoScroll != 0 ? GLG.GlgPanType.NO_PAN : GLG.GlgPanType.PAN_X );
    
   this.ChartVP.SetDResource( "Pan", ( pan_x | GLG.GlgPanType.PAN_Y_AUTO ) );
}

//////////////////////////////////////////////////////////////////////////////
// Changes the time span shown in the graph, adjusts major and minor tick 
// intervals to match the time span.
//////////////////////////////////////////////////////////////////////////////
GlgRealTimeChart.prototype.SetChartSpan = function( /*int*/ span_index )
{
   let span, major_interval, minor_interval;   /* int */
    
   /* Change chart's time span, as well as major and minor tick intervals.*/
   switch( span_index )
   {
    default:
    case 0:
      span = ONE_MINUTE;
      major_interval = 10;  /* major tick every 10 sec. */
      minor_interval = 1;   /* minor tick every sec. */
      break;
        
    case 1:
      span = 10 * ONE_MINUTE;
      major_interval = ONE_MINUTE * 2; /* major tick every tow minutes. */
      minor_interval = 30;             /* minor tick every 30 sec. */
      break;
        
    case 2:
      span = ONE_HOUR;
      major_interval = ONE_MINUTE * 10; /* major tick every 10 min. */
      minor_interval = ONE_MINUTE;      /* minor tick every min. */
      break;

    case 3:
      span = ONE_HOUR * 8;
      major_interval = ONE_HOUR;        /* major tick every hour. */
      minor_interval = ONE_MINUTE * 15; /* minor tick every 15 minutes. */
      break;
   }
    
   /* Update the menu in the drawing with the initial value if different. */
   this.Toolbar.SetDResourceIf( "SpanSelector/SelectedIndex", 
                                span_index, true );
        
   /* Set intervals before SetZoom() below to avoid redrawing huge number 
      of labels. 
   */
   this.Chart.SetDResource( "XAxis/MajorInterval", major_interval );
   this.Chart.SetDResource( "XAxis/MinorInterval", minor_interval );
    
   /* Set the X axis span which controls how much data is displayed in the 
      chart. 
   */
   this.TimeSpan = span;
   this.Chart.SetDResource( "XAxis/Span", this.TimeSpan );
    
   /* Turn on data filtering for large spans. FilterType and FilterPrecision
      attributes of all plots are constrained, so that they may be set in one
      place, on one plot.
   */
   if( span_index > 1 )
   {
      /* Aggregate multiple data samples to minimize a number of data points 
         drawn per each horizontal FilterPrecision interval.
         Show only one set of MIN/MAX values per each pixel interval. 
         An averaging data filter is also available.
      */
      this.PlotArray[0].plot.SetDResource( "FilterType", 
                                           GLG.GlgChartFilterType.MIN_MAX_FILTER );
      this.PlotArray[0].plot.SetDResource( "FilterPrecision", 1.0 );
   }
   else
     this.PlotArray[0].plot.SetDResource( "FilterType", 
                                          GLG.GlgChartFilterType.NULL_FILTER );
    
   /* Change time and tooltip formatting to match the selected span. */
   this.SetTimeFormats();
}

//////////////////////////////////////////////////////////////////////////////
// Sets the formats of time labels and tooltips depending on the selected 
// time span.
//////////////////////////////////////////////////////////////////////////////
GlgRealTimeChart.prototype.SetTimeFormats = function()
{
   let 
     time_label_format,           /* String */
     time_tooltip_format,         /* String */
     chart_tooltip_format;        /* String */

   /* No additional code is required to use the default settings defined 
      in the drawing. The code below illustrates advanced options for 
      customizing label and tooltip formatting when switching between 
      time spans and data display modes.
       
      For an even greater control over labels and tooltips, an application 
      can define custom Label and Tooltip formatters that will supply 
      custom strings for axis labels and tooltips.
   */
    
   /* Different time formats are used depending on the selected
      time span. See strftime() for all time format options.
   */
   switch( this.SpanIndex )
   {
    default:  /* 1 minute and 10 minutes spans */
      /* Use the preferred time and date display format for the current 
         locale. 
      */
      time_label_format = "%X%n%x";
      break;
        
    case 2: /* 1 hour span */
      /* Use the 12 hour time display with no seconds, and the default 
         date display format for the current locale.
      */
      time_label_format = "%I:%M %p%n%x";
      break;
        
    case 3: /* 1 hour and 8 hour spans */
      /* Use 24 hour notation and don't display seconds. */
      time_label_format = "%H:%M%n%x";
      break;
   }
    
   this.Chart.SetSResource( "XAxis/TimeFormat", time_label_format );
    
   /* Specify axis and chart tooltip format, if different from default 
      formats defined in the drawing.
   */
   time_tooltip_format = 
     "Time: <axis_time:%X> +0.<axis_time_ms:%03.0lf> sec.\nDate: <axis_time:%x>";
    
   /* <sample_time:%s> inherits time format from the X axis. */
   chart_tooltip_format = 
     "Plot <plot_string:%s> value= <sample_y:%.2lf>\n<sample_time:%s>";
    
   /* Set time label and tooltip formats. */
   this.Chart.SetSResource( "XAxis/TooltipFormat", time_tooltip_format );
   this.Chart.SetSResource( "TooltipFormat", chart_tooltip_format );
}

//////////////////////////////////////////////////////////////////////////////
// Scrolls the chart to the minimum or maximum time stamp to show the 
// most recent or the least recent data. If show_extra is True, adds a 
// few extra seconds in the real-time mode to show a few next updates
// without scrolling the chart.
//
// Enabling AutoScroll automatically scrolls the chart to show current 
// data points when the new time stamp is more recent then the EndValue 
// of the axis, but it is not the case when the chart is scrolled into 
// the future (to the right) - still need to invoke this method.
//////////////////////////////////////////////////////////////////////////////
GlgRealTimeChart.prototype.ScrollToDataEnd =
  function( /*int*/ data_end, /*boolean*/ show_extra_sec )
{
   let end_value, extra_sec;   /* double */
    
   if( data_end == DONT_CHANGE )
     return;
    
   // Get the min and max time stamp.
   let min_max =   /* GlgMinMax */
     this.Chart.GetChartDataExtent( null, /* x extent */ true,
                                    /*query all samples*/ false );
   if( min_max == null )
     return;
    
   if( show_extra_sec )   
     extra_sec = this.GetExtraSeconds();
   else
     extra_sec = 0.0;
    
   if( data_end == MOST_RECENT )
     end_value = min_max.max + extra_sec;
   else   /* LEAST_RECENT */
     end_value = min_max.min - extra_sec + this.TimeSpan ;
    
   this.Chart.UpdateChartTimeAxis( null, end_value, false );
}
   
//////////////////////////////////////////////////////////////////////////////
// Restore Y axis range to the initial Low/High values.
//////////////////////////////////////////////////////////////////////////////
GlgRealTimeChart.prototype.RestoreInitialYRanges = function()
{
   let axis_array = this.Chart.GetResourceObject( "YAxisGroup" );  /*GlgObject*/
    
   for( let i=0; i<this.NumYAxes; ++i )
   {
      let axis = axis_array.GetElement( i );   /* GlgObject */
      axis.SetDResource( "Low", this.Low[i] );
      axis.SetDResource( "High", this.High[i] );
   }
}
   
//////////////////////////////////////////////////////////////////////////////
// Returns true if the chart's viewport is in ZoomToMode.
// ZoomToMode is activated on Dragging and ZoomTo operations.
//////////////////////////////////////////////////////////////////////////////
GlgRealTimeChart.prototype.ZoomToMode = function()   /* boolean */
{
   let zoom_mode =    /* int */
     Math.trunc( this.ChartVP.GetDResource( "ZoomToMode" ) );
   return ( zoom_mode != 0 );
}

//////////////////////////////////////////////////////////////////////////////
// Abort ZoomTo mode.
//////////////////////////////////////////////////////////////////////////////
GlgRealTimeChart.prototype.AbortZoomTo = function()
{
   if( this.ZoomToMode() )
   {
      // Abort zoom mode in progress.
      this.ChartVP.SetZoom( null, 'e', 0.0 ); 
      this.ChartVP.Update();
   }
}

//////////////////////////////////////////////////////////////////////////////
// Determines a good number of extra seconds to be added at the end in
// the real-time mode to show a few next updates without scrolling the
// chart.
//////////////////////////////////////////////////////////////////////////////
GlgRealTimeChart.prototype.GetExtraSeconds = function()   /* double */
{
   let extra_sec, max_extra_sec;   /* double */

   extra_sec = this.TimeSpan * 0.1;
   max_extra_sec = ( this.TimeSpan > ONE_HOUR ? 5.0 : 3.0 );

   if( extra_sec > max_extra_sec )
     extra_sec = max_extra_sec;
   
   return extra_sec;
}

//////////////////////////////////////////////////////////////////////////////
// Changes drawing size while maintaining width/height aspect ratio.
//////////////////////////////////////////////////////////////////////////////
GlgRealTimeChart.prototype.SetDrawingSize = function( next_size )
{
   const ASPECT_RATIO = 700 / 540;
    
   const MIN_WIDTH = 500;
   const MAX_WIDTH = 900;
   const SCROLLBAR_WIDTH = 15;

   let span = document.body.clientWidth - SCROLLBAR_WIDTH;    

   if( this.SizeIndex == undefined )   // First time: initialize.
   {
      this.SizeIndex = 0;

      if( this.IsMobile )
      {
         /* Mobile devices use fixed device-width: disable Change Drawing Size 
            button. If it's not standalone, it is handled outside of this 
            script.
         */
         if( this.Standalone )
           RemoveElement( null, this.GLG_div_name + "_change_size" );
      }
      else   /* Desktop */
      {      
         const small_sizes  = [ 1, 1.5,  2.,   2.5 ];
         const medium_sizes = [ 1, 0.75, 1.25, 1.5 ];
         const large_sizes  = [ 1, 0.6,  1.25, 1.5 ];
         
         if( span < 600 )
           this.SetDrawingSize.size_array = small_sizes;
         else if( span < 800 )
           this.SetDrawingSize.size_array = medium_sizes;
         else
           this.SetDrawingSize.size_array = large_sizes;
         
         this.SetDrawingSize.num_sizes = this.SetDrawingSize.size_array.length;
         
         /* Handle browser zooming. */
         this.ResizeListener = ()=>this.SetDrawingSize( false );
         window.addEventListener( "resize", this.ResizeListener );
      }
   }
   else if( next_size )
   {
      ++this.SizeIndex;
      this.SizeIndex %= this.SetDrawingSize.num_sizes;
   }
    
   let drawing_area = document.getElementById( this.GLG_div_name );
   if( this.IsMobile )
   {
      /* Mobile devices use constant device-width, adjust only the height 
         of the drawing to keep the aspect ratio.
      */
      drawing_area.style.height =
        "" + Math.trunc( drawing_area.clientWidth / ASPECT_RATIO ) + "px";
   }
   else   /* Desktop */
   {
      let start_width;
      if( span < MIN_WIDTH )
        start_width = MIN_WIDTH;
      else if( span > MAX_WIDTH )
        start_width = MAX_WIDTH;
      else
        start_width = span;

      let size_coeff = this.SetDrawingSize.size_array[ this.SizeIndex ];
      let width = Math.trunc( Math.max( start_width * size_coeff, MIN_WIDTH ) );
      drawing_area.style.width = "" + width + "px";
      drawing_area.style.height = 
        "" + Math.trunc( width / ASPECT_RATIO ) + "px";
   }

   // Adjust canvas resolution for mobile devices and browser zoom state.
   if( !next_size )
     this.SetCanvasResolution();
}

//////////////////////////////////////////////////////////////////////////////
// Increases canvas resolution for mobile devices with HiDPI displays and for
// browser zooming. Sets CoordScale global variable.
//////////////////////////////////////////////////////////////////////////////
GlgRealTimeChart.prototype.SetCanvasResolution = function()
{
   let TextScale, PixelOffsetScale, ScreenCoordScale, NativeWidgetTextScale;

   if( this.IsMobile )
   {
      /* CoordScale parameter defines canvas coordinate scaling.
         Values greater than 1 increase canvas resolution and result in 
         sharper rendering. On mobile devices with devicePixelRatio > 1,
         the value of devicePixelRatio may be used for very crisp rendering
         with very thin lines. For pixel ration greater than 2, limit 
         CoordScale to 2 to draw thicker lines.

         CanvasScale > 1 makes text smaller. The TextScale parameter defines
         the text scaling factor used to increase text size.

         The PixelOffsetScale parameter is used to scale pixel offsets,
         such as pixel offsets used in layout of charts. When text is scaled,
         pixel offsets are usually scaled by the same factor to increase space
         allowed for the enlarged text.

         The ScreenCoordScale parameter specifies a scaling factor for fixed
         scale viewports that use screen coordinates. If the size of the fixed
         scale viewport (such as a height of a toolbar) is controlled by a pixel
         offset transformation, ScreenCoordScale may be set to the same value as
         PixelOffsetScale to scale the content of the viewport proportionally
         to its height increase.

         The NativeWidgetTextScale parameter defines the scaling factor that is
         used to scale down text in native widgets (such as native buttons, 
         toggles, etc.) to match the scale of the drawing.
      */
      if( window.devicePixelRatio > 2. )
        this.CoordScale = 2.;
      else
        this.CoordScale = window.devicePixelRatio;

      TextScale = 1.5;
      PixelOffsetScale = TextScale;
      ScreenCoordScale = PixelOffsetScale;
      NativeWidgetTextScale = 1.;
   }
   else   // Desktop
   {
      /* Change canvas resolution to match browser zoom state. */
      this.CoordScale = window.devicePixelRatio;      
      TextScale = window.devicePixelRatio;
      PixelOffsetScale = window.devicePixelRatio;
      ScreenCoordScale = window.devicePixelRatio;
      NativeWidgetTextScale = 1.;    // Don't change native text size.
   }

   GLG.SetCanvasScale( this.CoordScale, TextScale, NativeWidgetTextScale,
                       PixelOffsetScale, ScreenCoordScale );
}

//////////////////////////////////////////////////////////////////////////
function RemoveElement( parent_id, child_id )
{
   let element;
   if( parent_id == null )
     // No parent was provided: remove from document.
     element = document.getElementById( child_id );
   else
   {
      // Parent ID was provided: remove from parent.
      let parent = document.getElementById( parent_id );
      if( parent == null )
        return;
      element = parent.querySelector( "#" + child_id );
   }
   
   if( element != null )
     element.parentNode.removeChild( element );
}

//////////////////////////////////////////////////////////////////////////////
// Loads any assets required by the application and invokes the specified
// callback when done.
// Alternatively, the application drawing can be loaded as an asset here
// as well, so that it starts loading without waiting for the other assets 
// to finish loading.
//////////////////////////////////////////////////////////////////////////////
GlgRealTimeChart.prototype.LoadAssets = function( callback, user_data )
{
   Debug( "LoadAssets for: " + this.GLG_div_name );

   if( !this.Active )
     return;

   /* Define an internal variable to keep the number of loaded assets. */
   this.NumLoadedAssets = 0;

   /* HTML5 doesn't provide a scrollbar input element (only a range input 
      html element is available). This application needs to load GLG scrollbars
      used for integrated chart scrolling. For each loaded scrollbar, the 
      AssetLoaded callback is invoked with the supplied data array parameter.

      Using "bind( this )" as a shorter way to provide "this" compared with 
      using lambda: with bind, we do not need to specify parameter list that
      we would need to provide for lambda.
   */
   GLG.LoadWidgetFromURL( this.GetFullName( "scrollbar_h.g" ), null,
                          this.AssetLoaded.bind( this ),
                          { name: "scrollbar_h", callback: callback,
                            user_data: user_data },
                          /*abort test function*/ ()=>!this.Active );
   GLG.LoadWidgetFromURL( this.GetFullName( "scrollbar_v.g" ), null,
                          this.AssetLoaded.bind( this ),
                          { name: "scrollbar_v", callback: callback,
                            user_data: user_data },
                          /*abort test function*/ ()=>!this.Active );
}

//////////////////////////////////////////////////////////////////////////////
GlgRealTimeChart.prototype.AssetLoaded = function( glg_object, data, path )
{
   if( !this.Active )
     return;
   
   if( data.name == "scrollbar_h" )
   {
      if( glg_object != null )
        glg_object.SetResourceObject( "$config/GlgHScrollbar", glg_object );
   }
   else if( data.name == "scrollbar_v" )
   {
      if( glg_object != null )
        glg_object.SetResourceObject( "$config/GlgVScrollbar", glg_object );
   }
   else
     AppError( "Unexpected asset name" );

   ++this.NumLoadedAssets;
    
   // Invoke the callback after all assets have been loaded.
   if( this.NumLoadedAssets == 2 )
     data.callback( data.user_data );
}

//////////////////////////////////////////////////////////////////////////////
function Debug( message )
{
   if( DEBUG )
     AppLog( message );
}
